import { germanSeoSeeds } from "@/data/scriptario-seo-de";

export const scriptarioDefaultLocale = "en";

export const scriptarioLocales = [
  {
    code: "en",
    label: "English",
    shortLabel: "EN",
    flag: "🇺🇸",
  },
  {
    code: "es",
    label: "Español",
    shortLabel: "ES",
    flag: "🇪🇸",
  },
  {
    code: "pt-br",
    label: "Português (Brasil)",
    shortLabel: "PT-BR",
    flag: "🇧🇷",
  },
  {
    code: "de",
    label: "Deutsch",
    shortLabel: "DE",
    flag: "🇩🇪",
  },
  {
    code: "it",
    label: "Italiano",
    shortLabel: "IT",
    flag: "🇮🇹",
  },
] as const;

export type ScriptarioLocale = (typeof scriptarioLocales)[number]["code"];

export function isScriptarioLocale(value: string): value is ScriptarioLocale {
  return scriptarioLocales.some((locale) => locale.code === value);
}

export function normalizeScriptarioLocale(value: string | undefined | null): ScriptarioLocale {
  const normalized = String(value || "").trim().toLowerCase().replace(/_/g, "-");
  if (normalized.startsWith("de-")) return "de";
  return isScriptarioLocale(normalized) ? normalized : scriptarioDefaultLocale;
}

const scriptarioLocalizedRootPaths = new Set([
  "/",
  "/features",
  "/pricing",
  "/blog",
  "/contacts",
  "/privacy-policy",
  "/terms",
  "/refund",
  "/cookie-policy",
  "/extension-privacy",
]);

export const scriptarioOnboardingBlogSlugs: Record<ScriptarioLocale, string> = {
  en: "getting-started-with-scriptario",
  es: "guia-para-empezar-con-scriptario",
  "pt-br": "guia-para-comecar-no-scriptario",
  it: "guida-introduttiva-a-scriptario",
  de: "erste-schritte-mit-scriptario",
};

const scriptarioStaticAliases: Record<string, string> = {
  "/privacy.html": "/privacy-policy",
  "/terms.html": "/terms",
  "/refund.html": "/refund",
  "/cookies.html": "/cookie-policy",
  "/cookie-policy.html": "/cookie-policy",
};

const scriptarioSeoSlugMap: Array<Record<"en", string> & Partial<Record<ScriptarioLocale, string>>> = [
  { en: "video-link-analysis", es: "analisis-de-video-por-enlace", "pt-br": "analise-de-video-por-link" },
  { en: "audio-transcription", es: "transcripcion-de-audio", "pt-br": "transcricao-de-audio" },
  { en: "record-and-transcribe-meetings", es: "grabar-y-transcribir-reuniones", "pt-br": "gravar-e-transcrever-reunioes" },
  { en: "meeting-assistant", es: "asistente-de-reuniones", "pt-br": "assistente-de-reunioes" },
  { en: "audio-to-text", es: "audio-a-texto", "pt-br": "audio-para-texto" },
  { en: "audio-to-word", es: "audio-a-word", "pt-br": "audio-para-word" },
  { en: "audio-recording-to-text", es: "grabacion-de-audio-a-texto", "pt-br": "gravacao-de-audio-para-texto" },
  { en: "subtitle-generator", es: "generador-de-subtitulos", "pt-br": "gerador-de-legendas" },
  { en: "voice-to-text", es: "voz-a-texto", "pt-br": "voz-para-texto" },
  { en: "voice-message-to-text", es: "mensaje-de-voz-a-texto", "pt-br": "mensagem-de-voz-para-texto" },
  { en: "voice-message-transcription", es: "transcripcion-de-mensajes-de-voz", "pt-br": "transcricao-de-mensagens-de-voz" },
  { en: "extract-text-from-video", es: "extraer-texto-de-video", "pt-br": "extrair-texto-de-video" },
  { en: "video-notes", es: "notas-de-video", "pt-br": "notas-de-video" },
  { en: "convert-audio-to-text", es: "convertir-audio-a-texto", "pt-br": "converter-audio-para-texto" },
  { en: "m4a-to-text", es: "m4a-a-texto", "pt-br": "m4a-para-texto" },
  { en: "mp3-to-text", es: "mp3-a-texto", "pt-br": "mp3-para-texto" },
  { en: "mp3-to-txt", es: "mp3-a-txt", "pt-br": "mp3-para-txt" },
  { en: "mp4-to-text", es: "mp4-a-texto", "pt-br": "mp4-para-texto" },
  { en: "ai-audio-to-text", es: "audio-a-texto-con-ia", "pt-br": "audio-para-texto-com-ia" },
  { en: "ai-audio-transcription", es: "transcripcion-de-audio-con-ia", "pt-br": "transcricao-de-audio-com-ia" },
  { en: "ogg-to-text", es: "ogg-a-texto", "pt-br": "ogg-para-texto" },
  { en: "video-summary", es: "resumen-de-video", "pt-br": "resumo-de-video" },
  { en: "youtube-summary", es: "resumen-de-youtube", "pt-br": "resumo-de-youtube" },
  { en: "transcribe-audio-to-text", es: "transcribir-audio-a-texto", "pt-br": "transcrever-audio-para-texto" },
  { en: "transcribe-video-to-text", es: "transcribir-video-a-texto", "pt-br": "transcrever-video-para-texto" },
  { en: "english-audio-translation", es: "traduccion-de-audio-en-ingles", "pt-br": "traducao-de-audio-em-ingles" },
  { en: "chinese-audio-translation", es: "traduccion-de-audio-en-chino", "pt-br": "traducao-de-audio-em-chines" },
  { en: "audio-translation-to-text", es: "traduccion-de-audio-a-texto", "pt-br": "traducao-de-audio-para-texto" },
  { en: "voice-translation-to-text", es: "traduccion-de-voz-a-texto", "pt-br": "traducao-de-voz-para-texto" },
  { en: "song-to-text", es: "cancion-a-texto", "pt-br": "musica-para-texto" },
  { en: "speech-to-text-converter", es: "convertidor-de-voz-a-texto", "pt-br": "conversor-de-fala-para-texto" },
  { en: "audio-to-text-software", es: "software-de-audio-a-texto", "pt-br": "software-de-audio-para-texto" },
  { en: "speech-recognition", es: "reconocimiento-de-voz", "pt-br": "reconhecimento-de-fala" },
  { en: "transcribe-video-to-text-online", es: "transcribir-video-a-texto-online", "pt-br": "transcrever-video-para-texto-online" },
  { en: "audio-transcription-to-text", es: "transcripcion-de-audio-a-texto", "pt-br": "transcricao-de-audio-para-texto" },
  { en: "voice-recorder-transcription", es: "transcripcion-de-grabadora-de-voz", "pt-br": "transcricao-de-gravador-de-voz" },
  { en: "interview-transcription", es: "transcripcion-de-entrevistas", "pt-br": "transcricao-de-entrevistas" },
  { en: "video-transcription", es: "transcripcion-de-video", "pt-br": "transcricao-de-video" },
  { en: "transcription-tool", es: "herramienta-de-transcripcion", "pt-br": "ferramenta-de-transcricao" },
  { en: "speech-to-text-online", es: "voz-a-texto-online", "pt-br": "fala-para-texto-online" },
  { en: "meeting-summary", es: "resumen-de-reunion", "pt-br": "resumo-de-reuniao" },
  { en: "online-video-to-text", es: "video-online-a-texto", "pt-br": "video-online-para-texto" },
  { en: "subtitles-for-video", es: "subtitulos-para-video", "pt-br": "legendas-para-video" },
  { en: "audio-to-subtitles", es: "audio-a-subtitulos", "pt-br": "audio-para-legendas" },
  { en: "text-from-video", es: "texto-de-video", "pt-br": "texto-de-video" },
  { en: "audio-to-text-transcription", es: "transcripcion-de-audio-a-texto-online", "pt-br": "transcricao-de-audio-para-texto-online" },
  { en: "podcast-transcription", es: "transcripcion-de-podcast", "pt-br": "transcricao-de-podcast" },
  { en: "meeting-call-transcription", es: "transcripcion-de-reuniones-y-llamadas", "pt-br": "transcricao-de-reunioes-e-chamadas" },
  { en: "video-to-text-transcription", es: "transcripcion-de-video-a-texto", "pt-br": "transcricao-de-video-para-texto" },
  { en: "recording-transcription", es: "transcripcion-de-grabaciones", "pt-br": "transcricao-de-gravacoes" },
  { en: "call-transcription", es: "transcripcion-de-llamadas", "pt-br": "transcricao-de-chamadas" },
  { en: "video-transcription-online", es: "transcripcion-de-video-online", "pt-br": "transcricao-de-video-online" },
  { en: "transcriptor", es: "transcriptor", "pt-br": "transcritor" },
  { en: "video-to-srt", es: "video-a-srt", "pt-br": "video-para-srt" },
  { en: "video-to-text", es: "video-a-texto", "pt-br": "video-para-texto" },
  { en: "social-video-to-text", es: "video-social-a-texto", "pt-br": "video-social-para-texto" },
  { en: "wav-to-text", es: "wav-a-texto", "pt-br": "wav-para-texto" },
  { en: "youtube-to-text", es: "youtube-a-texto", "pt-br": "youtube-para-texto" },
  { en: "youtube-video-to-text", es: "video-de-youtube-a-texto", "pt-br": "video-do-youtube-para-texto" },
  { en: "whisper-alternative", es: "alternativa-a-whisper", "pt-br": "alternativa-ao-whisper" },
  { en: "otter-ai-alternative", es: "alternativa-a-otter-ai", "pt-br": "alternativa-ao-otter-ai" },
  { en: "notta-alternative", es: "alternativa-a-notta", "pt-br": "alternativa-ao-notta" },
  { en: "turboscribe-alternative", es: "alternativa-a-turboscribe", "pt-br": "alternativa-ao-turboscribe" },
  // { en: "speech2text-alternative", es: "alternativa-a-speech2text", "pt-br": "alternativa-ao-speech2text" },  // снято с публикации 2026-10-02, 404 вместо страницы
  { en: "any2text-alternative", es: "alternativa-a-any2text", "pt-br": "alternativa-ao-any2text" },
  { en: "fireflies-alternative", es: "alternativa-a-fireflies", "pt-br": "alternativa-ao-fireflies" },
  { en: "transcriber", es: "transcriptor-de-audio", "pt-br": "transcritor-de-audio" },
  { en: "sound-to-text", es: "sonido-a-texto", "pt-br": "som-para-texto" },
  { en: "speech-to-text", es: "habla-a-texto", "pt-br": "fala-para-texto" },
];

const scriptarioItalianSeoSlugs: Record<string, string> = {
  "video-link-analysis": "analisi-video-da-link",
  "audio-transcription": "trascrizione-audio",
  "record-and-transcribe-meetings": "registrare-e-trascrivere-riunioni",
  "meeting-assistant": "assistente-per-riunioni",
  "audio-to-text": "audio-in-testo",
  "audio-to-word": "audio-in-word",
  "audio-recording-to-text": "registrazione-audio-in-testo",
  "subtitle-generator": "generatore-di-sottotitoli",
  "voice-to-text": "voce-in-testo",
  "voice-message-to-text": "messaggio-vocale-in-testo",
  "voice-message-transcription": "trascrizione-messaggi-vocali",
  "extract-text-from-video": "estrarre-testo-da-video",
  "video-notes": "appunti-da-video",
  "convert-audio-to-text": "convertire-audio-in-testo",
  "m4a-to-text": "m4a-in-testo",
  "mp3-to-text": "mp3-in-testo",
  "mp3-to-txt": "mp3-in-txt",
  "mp4-to-text": "mp4-in-testo",
  "ai-audio-to-text": "audio-in-testo-con-ai",
  "ai-audio-transcription": "trascrizione-audio-con-ai",
  "ogg-to-text": "ogg-in-testo",
  "video-summary": "riassunto-video",
  "youtube-summary": "riassunto-youtube",
  "transcribe-audio-to-text": "trascrivere-audio-in-testo",
  "transcribe-video-to-text": "trascrivere-video-in-testo",
  "english-audio-translation": "traduzione-audio-inglese",
  "chinese-audio-translation": "traduzione-audio-cinese",
  "audio-translation-to-text": "traduzione-audio-in-testo",
  "voice-translation-to-text": "traduzione-voce-in-testo",
  "song-to-text": "canzone-in-testo",
  "speech-to-text-converter": "convertitore-voce-in-testo",
  "audio-to-text-software": "software-audio-in-testo",
  "speech-recognition": "riconoscimento-vocale",
  "transcribe-video-to-text-online": "trascrivere-video-in-testo-online",
  "audio-transcription-to-text": "trascrizione-audio-in-testo",
  "voice-recorder-transcription": "trascrizione-registratore-vocale",
  "interview-transcription": "trascrizione-interviste",
  "video-transcription": "trascrizione-video",
  "transcription-tool": "strumento-di-trascrizione",
  "speech-to-text-online": "voce-in-testo-online",
  "meeting-summary": "riassunto-riunione",
  "online-video-to-text": "video-online-in-testo",
  "subtitles-for-video": "sottotitoli-per-video",
  "audio-to-subtitles": "audio-in-sottotitoli",
  "text-from-video": "testo-da-video",
  "audio-to-text-transcription": "trascrizione-audio-testo",
  "podcast-transcription": "trascrizione-podcast",
  "meeting-call-transcription": "trascrizione-chiamate-riunioni",
  "video-to-text-transcription": "trascrizione-video-in-testo",
  "recording-transcription": "trascrizione-registrazioni",
  "call-transcription": "trascrizione-chiamate",
  "video-transcription-online": "trascrizione-video-online",
  "transcriptor": "trascrittore",
  "video-to-srt": "video-in-srt",
  "video-to-text": "video-in-testo",
  "social-video-to-text": "video-social-in-testo",
  "wav-to-text": "wav-in-testo",
  "youtube-to-text": "youtube-in-testo",
  "youtube-video-to-text": "video-youtube-in-testo",
  "whisper-alternative": "alternativa-a-whisper",
  "otter-ai-alternative": "alternativa-a-otter-ai",
  "notta-alternative": "alternativa-a-notta",
  "turboscribe-alternative": "alternativa-a-turboscribe",
  // "speech2text-alternative": "alternativa-a-speech2text",  // снято с публикации 2026-10-02, 404 вместо страницы
  "any2text-alternative": "alternativa-a-any2text",
  "fireflies-alternative": "alternativa-a-fireflies",
  "transcriber": "trascrivere",
  "sound-to-text": "suono-in-testo",
  "speech-to-text": "parlato-in-testo",
};

const scriptarioEnglishSlugByItalianSlug = Object.fromEntries(
  Object.entries(scriptarioItalianSeoSlugs).map(([englishSlug, italianSlug]) => [italianSlug, englishSlug]),
) as Record<string, string>;
const scriptarioEnglishSlugByGermanSlug = Object.fromEntries(
  Object.entries(germanSeoSeeds).map(([englishSlug, seed]) => [seed.slug, englishSlug]),
) as Record<string, string>;

export function getScriptarioLocalePrefix(locale: ScriptarioLocale = scriptarioDefaultLocale) {
  return `/${locale}`;
}

export function getScriptarioHomePath(locale: ScriptarioLocale = scriptarioDefaultLocale) {
  return getScriptarioLocalePrefix(locale);
}

function normalizePath(path: string) {
  const withoutQuery = path.split("?")[0]?.split("#")[0] || "/";
  const withLeadingSlash = withoutQuery.startsWith("/") ? withoutQuery : `/${withoutQuery}`;
  const withoutTrailingSlash = withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, "") : withLeadingSlash;

  return scriptarioStaticAliases[withoutTrailingSlash] || withoutTrailingSlash;
}

export function getScriptarioLocaleFromPath(path: string): ScriptarioLocale {
  const normalizedPath = normalizePath(path);
  const segment = normalizedPath.split("/")[1] || "";

  return isScriptarioLocale(segment) ? segment : scriptarioDefaultLocale;
}

function splitLocalizedPath(path: string): { locale: ScriptarioLocale; restPath: string } {
  const normalizedPath = normalizePath(path);
  const [, maybeLocale, ...restSegments] = normalizedPath.split("/");

  if (isScriptarioLocale(maybeLocale)) {
    return {
      locale: maybeLocale,
      restPath: restSegments.length > 0 ? `/${restSegments.join("/")}` : "/",
    };
  }

  return {
    locale: scriptarioDefaultLocale,
    restPath: normalizedPath,
  };
}

export function getScriptarioLocalizedPath(path: string, locale: ScriptarioLocale = scriptarioDefaultLocale) {
  const prefix = getScriptarioLocalePrefix(locale);
  const { locale: currentLocale, restPath } = splitLocalizedPath(path || "/");

  if (currentLocale === locale && (restPath === "/" || path.startsWith(prefix))) {
    return normalizePath(path);
  }

  if (restPath === "/") {
    return prefix;
  }

  if (scriptarioLocalizedRootPaths.has(restPath)) {
    return `${prefix}${restPath}`;
  }

  if (restPath.startsWith("/blog/")) {
    const currentSlug = restPath.slice("/blog/".length);
    if (scriptarioOnboardingBlogSlugs[currentLocale] === currentSlug) {
      return `${prefix}/blog/${scriptarioOnboardingBlogSlugs[locale]}`;
    }
  }

  const slug = restPath.slice(1);
  const canonicalSlug = currentLocale === "it" ? scriptarioEnglishSlugByItalianSlug[slug] || slug
    : currentLocale === "de" ? scriptarioEnglishSlugByGermanSlug[slug] || slug : slug;
  const seoPage = scriptarioSeoSlugMap.find((page) => (page[currentLocale] || page.en) === canonicalSlug);

  if (seoPage) {
    const localizedSlug = locale === "it" ? scriptarioItalianSeoSlugs[seoPage.en]
      : locale === "de" ? germanSeoSeeds[seoPage.en]?.slug : seoPage[locale];
    return `${prefix}/${localizedSlug || seoPage.en}`;
  }

  return prefix;
}
